import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Search, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { Avatar, Field, Input, PrimaryButton, Sheet, Spinner } from "@/components/peeps/ui";

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [admin, setAdmin] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (query) => {
    setLoading(true);
    try { setUsers(await api.adminListUsers(query)); } catch (e) { toast(e.message || "Couldn't load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user && !user.is_app_admin) { navigate("/profile", { replace: true }); return; }
    load("");
  }, [user, load, navigate]);

  const openEdit = (u) => { setEdit(u); setName(u.display_name || ""); setBio(u.bio || ""); setAdmin(!!u.is_app_admin); };
  const save = async () => {
    setSaving(true);
    try {
      await api.adminUpdateUser(edit.id, { display_name: name.trim(), bio: bio.trim(), is_app_admin: admin });
      toast("User updated");
      setEdit(null);
      await load(q);
    } catch (e) { toast(e.message || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen max-w-2xl mx-auto pb-16" style={{ background: colors.surface }}>
      <div className="flex items-center gap-2 px-3 pt-4 pb-2">
        <button data-testid="admin-back" onClick={() => navigate(-1)} className="rounded-full p-1.5" style={{ background: colors.surfaceSecondary }}>
          <ChevronLeft size={22} color={colors.onSurface} />
        </button>
        <h1 className="font-display font-bold text-[20px]" style={{ color: colors.onSurface }}>Manage users</h1>
      </div>

      <div className="px-4 mt-2">
        <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 mb-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
          <Search size={16} color={colors.muted} />
          <input data-testid="admin-user-search" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Search name or email" className="flex-1 bg-transparent outline-none font-display text-[14px]" style={{ color: colors.onSurface }} />
        </div>

        {loading ? <div className="flex justify-center py-10"><Spinner /></div> : (
          <div className="flex flex-col gap-2">
            {users.map((u) => (
              <button key={u.id} data-testid={`admin-user-${u.id}`} onClick={() => openEdit(u)} className="flex items-center gap-3 rounded-xl p-3 text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                <Avatar name={u.display_name} uri={u.profile_photo_url} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-display font-bold text-[14px] truncate" style={{ color: colors.onSurface }}>{u.display_name}</p>
                    {u.is_app_admin && <ShieldCheck size={13} color={colors.brandSecondary} />}
                  </div>
                  <p className="font-mono2 text-[11.5px] truncate" style={{ color: colors.faint }}>{u.email}</p>
                </div>
                <Pencil size={15} color={colors.muted} />
              </button>
            ))}
          </div>
        )}
      </div>

      {edit && (
        <Sheet visible={!!edit} onClose={() => setEdit(null)} title={`Edit ${edit.display_name || "user"}`} testID="admin-edit-sheet">
          <Field label="Display name"><Input testID="admin-edit-name" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Bio"><Input testID="admin-edit-bio" value={bio} onChange={(e) => setBio(e.target.value.slice(0, 160))} /></Field>
          <button data-testid="admin-toggle-admin" onClick={() => setAdmin(!admin)} className="flex items-center justify-between rounded-xl p-3.5 mb-4 w-full" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <span className="font-display font-medium text-[14px]" style={{ color: colors.onSurface }}>App admin</span>
            <span className="relative inline-flex items-center rounded-full" style={{ width: 42, height: 24, background: admin ? colors.brand : colors.surfaceTertiary }}>
              <span className="absolute rounded-full bg-white" style={{ width: 18, height: 18, top: 3, left: 3, transform: admin ? "translateX(18px)" : "translateX(0)", transition: "transform .15s" }} />
            </span>
          </button>
          <PrimaryButton testID="admin-save-user" label="Save" onClick={save} disabled={!name.trim()} loading={saving} />
        </Sheet>
      )}
    </div>
  );
}
